import {defineStore} from "pinia";
import {SubscriptionService} from "../services/subscription.service";
import {ApplicationHelper} from "../helpers/ApplicationHelper";

export const IntervalMonth = "month";
export const IntervalWeek = "week";
export const IntervalDay = "day";

export const IntervalYear = "year";

export const useSubscriptionStore = defineStore("subscription", {
  state: () => ({
    subscriptionService: null,
    subscriptions: [],
    funds: [],
    organizationId: null,
    loading: true, // Index loading flag
    loadingRecord: true, // Record loading flag
    currentOrganization: "null",
    showOfflineModal: false,
    perPage: 10,
    currentSubscription: {},
    fundsLoaded: false,
    editing: false,
    givingBlind: false,
    meta: {
      page: 1,
      pages: 1
    },
    filter: {
      sort: {
        key: "created_at",
        order: "desc"
      },
      search: {
        key: "user_email_cont", // Ransack search query, add more here to expand the search results
        term: null
      },
      filters: { // Dropdown select filters, need to instantiate initial value
        status: '',
        payment_method: '',
        interval: '',
        interval_count: ''
      }
    },
    permissions: {},
  }),

  actions: {
    setOrganizationId(id) {
      if (id) {
        this.organizationId = id;
        this.subscriptionService = new SubscriptionService(id);
      } else {
        throw `Organization ID is not set: ${id}`;
      }
    },
    async loadData() {
      try {
        this.subscriptions = [];
        this.page = 1;
        this.meta = {
          page: 1,
          pages: 1
        };
        await this.getSubscriptions();
      } catch (e) {
        console.error("Error loading data", e);
      }
    },


    async getSubscriptions() {
      this.loading = true;
      let params = new URLSearchParams();
      // params.set('query[organization_id_eq]', this.organizationId);
      const res = await this.subscriptionService.paginate(this.meta.page, params, this.filter);
      this.subscriptions = res.data;
      this.meta = res.meta;
      this.permissions = res.permissions;
      this.loading = false;

    },

    async getSubscription(id) {
      this.loadingRecord = true;
      this.editing = false;
      const res = await this.subscriptionService.show(id);
      this.currentSubscription = res.record;
      this.permissions = {...this.permissions, ...res.permissions};
      this.givingBlind = res.giving_blind || false;
      this.loadingRecord = false;
      return this.currentSubscription;
    },


    async toggleEdit() {
      if (this.editing) {
        this.editing = false;
      } else {
        this.loadingRecord = true;
        this.loadingRecord = false;
        this.localSubscription = {...this.currentSubscription};
        this.editing = true;
      }
    },

    async saveSubscription() {
      this.loadingRecord = true;
      let params = {
        subscription: {
          ...this.localSubscription,
        }
      };
      await this.subscriptionService.update(this.currentSubscription.id, params);
      await this.getSubscription(this.currentSubscription.id);
      await this.getSubscriptions();
    },

    cancelSubscription(subscription) {
      if (subscription?.organization_id) this.setOrganizationId(subscription.organization_id);
      if (!this.subscriptionService) return Promise.resolve(false);
      try {
        return this.subscriptionService.cancelSubscription(subscription.id);
      } catch {
        return Promise.resolve(false);
      }
    },

    pauseSubscription(subscription) {
      if (subscription?.organization_id) this.setOrganizationId(subscription.organization_id);
      if (!this.subscriptionService) return Promise.resolve(false);
      try {
        return this.subscriptionService.pauseSubscription(subscription.id);
      } catch {
        return Promise.resolve(false);
      }
    },

    resumeSubscription(subscription) {
      if (subscription?.organization_id) this.setOrganizationId(subscription.organization_id);
      if (!this.subscriptionService) return Promise.resolve(false);
      try {
        return this.subscriptionService.resumeSubscription(subscription.id);
      } catch {
        return Promise.resolve(false);
      }
    },
    // Sets the Ransack sort terms based on the passed key
    // This gets passed to index method to sort the paginated results
    sort(key) {
      if (this.filter.sort.key === key) {
        // If the passed key is the current key, flip the current sort order
        this.filter.sort.order = this.filter.sort.order === 'asc' ? 'desc' : 'asc';
      } else {
        // Otherwise set the new key and order by Ascending
        this.filter.sort.key = key;
        this.filter.sort.order = 'asc';
      }

      this.getSubscriptions();
    },

    search(term) {
      this.filter.search.term = term;
      this.getSubscriptions();
    },

    filterRecords(key, value) {
      this.filter.filters[key] = value;
      if (key === 'frequency') this.setFrequencyFilterValues(value);
      if(key === 'payment_method') this.setProcessorFilters(value);
      if(key === 'status') this.setStatusFilters(value);

      return this.getSubscriptions();
    },

    // Custom filter for payment processor type
    setProcessorFilters(value) {
      let processorType = null;
      switch (value) {
        case 'Offline':
          processorType = "Processor::OfflineProcessor";
          break;
        case 'Stripe':
          processorType = "Processor::StripeProcessor";
          break;
        case 'Helcim':
          processorType = "Processor::HelcimProcessor";
          break;
      }
      this.filter.filters['donations_payments_payment_processor_type'] = processorType;
    },

    // Translate subscription statuses TODO constantize
    setStatusFilters(value) {
      const map = {
        active: 'active',
        upcoming: 'trialing',
        paused: 'paused',
        canceled: 'cancelled',
        cancelled: 'cancelled'
      };
      this.filter.filters['status'] = map[value] || '';
    },

    // Interval is a computed value, adding a custom filter for this
    setFrequencyFilterValues(value) {
      let interval = null;
      let intervalCount = null;
      switch (value) {
        case "Weekly":
          interval = 'week';
          intervalCount = 1;
          break;
        case "Bi-Weekly":
          interval = 'week';
          intervalCount = 2;
          break;
        case "Monthly":
          interval = 'month';
          intervalCount = 1;
          break;
        case "Quarterly":
          interval = 'month';
          intervalCount = 3;
          break;
        case "Yearly":
          interval = 'year';
          break;
        default:
          interval = null;
          intervalCount = null;
          break;
      }
      this.filter.filters['interval'] = interval;
      this.filter.filters['interval_count'] = intervalCount;
    },
    nextPage() {
      if (this.loading) return;
      this.meta.page++;
      this.getSubscriptions();
    },
    prevPage() {
      if (this.loading) return;
      this.meta.page--;
      this.getSubscriptions();
    },
    moveToPage(page) {
      if (this.loading) return;
      this.meta.page = page;
      this.getSubscriptions();
    }
  },

  getters: {
    /**
     * Method used in generic Dashboard Index component.
     * Returns an object containing the index table data.
     * @param state
     */
    getIndexTableData(state) {
      let labels = [
        // {label: 'Name', key: 'name', sort: true},
        {label: 'User', key: 'email', ransackKey: 'user_email', sort: true},
        {label: 'Amount', key: 'amount', format: 'money', sort: true},
        {label: 'Designation', key: 'target_name', sort: false},
        {label: "Organization", key: 'organization_name', sort: true, ransackKey: "organization_name"},
        {
          label: 'Frequency',
          key: 'frequency',
          sort: false,
          select: true,
          options: ['Weekly', 'Bi-Weekly', 'Monthly', 'Quarterly', 'Yearly']
        },
        {
          label: 'Status',
          key: 'status',
          format: 'tags',
          sort: false,
          select: true,
          options: ['active', 'upcoming', 'paused','canceled', 'error']
        },
        {
          label: 'Payment Method', key: 'payment_method', sort: false,
          select: true,
          options: ['Offline', 'Helcim', 'Stripe']
        },
        {label: 'Date', key: 'created_at', format: 'date', sort: true},
      ];

      if (state.subscriptions.length > 0) {
        let subscriptions = state.subscriptions.map((subscription) => {
          return {
            ...subscription,
            email: subscription.user.email,
            payment_method: ApplicationHelper.formatPaymentProcessorType(subscription.processor_type), // TODO format
            frequency: this.intervalString(subscription),
            status: [this.statusString(subscription)?.toUpperCase()]
          };
        });
        return {
          labels: labels,
          rows: subscriptions
        };
      } else {
        return {
          labels: labels,
          rows: []
        };
      }
    },
    /**
     * Method used in generic Dashboard Show component.
     * Returns an object containing the record table data.
     * @param state
     */
    getShowTableData(state) {
      let labels = [
        'label',
        'data',
      ];
      if (state.currentRecord && Object.hasOwn(state.currentRecord, 'id')) {
        let rows = [
          {
            label: 'Name',
            data: state.currentRecord.name
          },
          {
            label: 'Fund',
            data: state.currentRecord.fund_name
          },
          {
            label: 'Description',
            data: state.currentRecord.description
          },
          {
            label: 'Start Date',
            data: {
              format: 'date',
              value: state.currentRecord.start_date
            }
          },
          {
            label: 'End Date',
            data: {
              format: 'date',
              value: state.currentRecord.end_date
            }
          },
          {
            label: "Donation Suggestions",
            data: {
              format: 'boolean',
              value: state.currentRecord.donation_suggestions_enabled,
              trueValue: "Enabled",
              falseValue: "Disabled"
            }
          },
          {
            label: "Suggested Donations",
            data: {
              format: 'array',
              value: state.currentRecord.donation_suggestions,
              display: function (val) {
                return "$" + val.toString();
              } // Display Formatting Closure
            }
          },
          {
            label: "Donor Wall",
            data: {
              format: 'boolean',
              value: state.currentRecord.donor_wall_enabled,
              trueValue: "Enabled",
              falseValue: "Disabled"
            }
          },
          {
            label: 'Target Amount',
            data: {
              format: 'money',
              value: state.currentRecord.donation_target
            }
          },
          {
            label: 'Total Gifts',
            data: state.currentRecord.total_contributions
          },
          {
            label: 'Total Dollars',
            data: {
              format: 'money',
              value: state.currentRecord.all_time_contribution_total
            }
          },
          {
            label: 'Latest Gift',
            data: {
              format: 'date',
              value: (state.currentRecord.last_donation) ? state.currentRecord.last_donation.created_at : ''
            }
          },
          {
            label: 'Latest Gift Amount',
            data: {
              format: 'money',
              value: (state.currentRecord.last_donation) ? state.currentRecord.last_donation.amount : ''
            }
          },

          {
            label: 'Banner Image',
            data: {
              format: 'image',
              value: state.currentRecord.banner_image_url
            }
          }
        ];
        return {
          labels: labels,
          rows: rows
        };
      } else {
        return {
          labels: labels,
          rows: []
        };
      }
    },

    intervalString: state => (subscription) => {
      if (!subscription) return "";
      if (subscription.interval && subscription.interval_count) {
        let count = subscription.interval_count;
        switch (subscription.interval) {
          case IntervalMonth:
            if (count === 1) return "Monthly";
            if (count === 2) return "Bi-Monthly";
            if (count === 3) return "Quarterly";
            break;
          case IntervalWeek:
            if (count === 1) return "Weekly";
            if (count === 2) return "Bi-Weekly";
            break;
          case IntervalDay:
            if (count === 1) return "Daily";
            break;
          case IntervalYear:
            return "Yearly";
          default:
            return "N/A";
        }
      } else {
        return "N/A";
      }
    },

    // Translates the subscription status into more readable format
    // Only using for 'trialing' -> which is a delayed subscription right now, might need more in future
    statusString: state => (subscription) => {
      if (!subscription) return "";
        switch (subscription.status) {
          case 'trialing':
           return 'upcoming';
          case 'cancelled':
            return 'canceled';
          default:
            return subscription.status;
        }
    },
  }
});
